/**
 * ChartGaugeComponent
 *
 * This component renders a gauge chart using Chart.js. It extends ChartCommon
 * and provides configurable options such as min, max, value, colors, and display settings.
 *
 * The gauge visually represents progress as a doughnut chart with a cutout,
 * showing percentage, value, and units as needed.
 */
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  signal,
} from '@angular/core';
import { ChartBase } from '../base/base.chart';
import {
  ChartData,
  ChartJsType,
  ChartOptions,
} from '../../models/chart.models';
import { gaugePlugin } from '../../plugins/gauge.plugins';
import { ChartCommon } from '../chart/chart.common';
import { TranslocoDecimalPipe } from '@jsverse/transloco-locale';
import { NgClass } from '@angular/common';

@Component({
  selector: 'chart-gauge',
  standalone: true,
  imports: [ChartBase, TranslocoDecimalPipe, NgClass],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="w-full h-auto flex flex-col items-center px-4">
      <div class="relative w-full max-w-xs">
        @if (type() && data()!.datasets) {
          <chart-base
            [type]="type()"
            [data]="data()"
            [options]="optionsComputed()"
            [plugins]="plugins()"
          />
        }
        <div
          class="absolute top-1/2 left-1/2 flex flex-col items-center gap-1 transform -translate-x-1/2 -translate-y-1/4"
        >
          <span class="text-2xl font-medium">
            @if (showPercentage()) {
              {{ valuePercentage() | translocoDecimal }}%
            }
            @if (showValue()) {
              {{ value() | translocoDecimal }} {{ units() }}
            }
          </span>
          @if (showBadge()) {
            <span
              class="text-xs px-2 py-0.5 border border-gray-300 bg-gray-100 rounded-full"
            >
              {{ value() | translocoDecimal }} {{ units() }}
            </span>
          }
          @if (showVariations()) {
            <span
              class="text-xs px-2 py-0.5 font-semibold rounded-full"
              [ngClass]="
                variations() >= 0
                  ? 'bg-lime-100 text-green'
                  : 'bg-red-100 text-red-400'
              "
            >
              {{ variations() | translocoDecimal }}%
            </span>
          }
        </div>
      </div>
      @if (showMaxMin()) {
        <div class="relative w-full mt-2 max-w-xs mb-4">
          <div
            class="absolute inset-x-0 flex justify-between text-gray-400 text-xs"
          >
            <span>
              {{ min() | translocoDecimal }} {{ unitsMin() || units() }}
            </span>
            <span>
              {{ max() | translocoDecimal }} {{ unitsMax() || units() }}
            </span>
          </div>
        </div>
      }
    </div>
  `,
})
export class ChartGaugeComponent extends ChartCommon {
  /** Minimum value of the gauge. */
  min = input.required<number>();
  /** Maximum value of the gauge. */
  max = input.required<number>();
  /** Current value to display in the gauge. */
  value = input.required<number>();
  /** Color for the progress section of the gauge. */
  progressColor = input<string>('#2B2B2B');
  /** Color for the remaining section of the gauge. */
  remainingColor = input<string>('#F0F1F3');
  /** Determines whether to show the numeric value. */
  showValue = input<boolean>(false);
  /** Determines whether to show the percentage. */
  showPercentage = input<boolean>(true);
  /** Determines whether to show the badge with the value. */
  showBadge = input<boolean>(true);
  /** Units to display next to the value. */
  units = input<string>();
  /** Optional max value units, if different from `units`. */
  unitsMax = input<string>();
  /** Optional min value units, if different from `units`. */
  unitsMin = input<string>();
  /** Determines whether to show the max and min values on the gauge. */
  showMaxMin = input<boolean>(true);
  /** Determines whether to show differences indicator. */
  showVariations = input<boolean>(false);
  /** Variations value to display differences indicator. */
  variations = input<number>(0);

  /** Computes the value percentage relative to the max value. */
  valuePercentage = computed<number>(() =>
    parseFloat(((this.value() / this.max()) * 100).toFixed(1)),
  );

  /** Type of chart to render, defaults to 'doughnut'. */
  type = signal<ChartJsType>('doughnut');

  /** Computes the cutout size dynamically based on screen width. */
  cutoutSize = computed(() => (window.innerWidth < 500 ? '85%' : '92%'));

  /**
   * `options`: Optional input to provide additional configuration options.
   */
  options = input<ChartOptions<'doughnut'>>();

  /** Chart.js options configuration for the gauge. */
  optionsComputed = computed<ChartOptions<'doughnut'>>(() => ({
    aspectRatio: 1.8,
    maintainAspectRatio: false,
    cutout: this.cutoutSize(),
    responsive: true,
    rotation: -90,
    circumference: 180,
    plugins: {
      tooltip: { enabled: false },
      legend: { display: false },
    },
  }));

  /** Plugins used for the chart, including a gauge plugin. */
  plugins = signal([gaugePlugin]);

  /** Signal to hold the chart data. */
  data = signal<ChartData<'doughnut'>>({} as any);

  constructor() {
    super();

    /** Effect to update chart data whenever the value changes. */
    effect(() => {
      const progress = this.valuePercentage();
      Promise.resolve().then(() =>
        this.data.set({
          labels: ['Progress', 'Remaining'],
          datasets: [
            {
              data: [progress, 100 - progress],
              backgroundColor: [this.progressColor(), this.remainingColor()],
              hoverBackgroundColor: [
                this.progressColor(),
                this.remainingColor(),
              ],
              borderColor: [this.progressColor(), ''],
              borderRadius: [80, 80],
              borderWidth: [7, 0],
            },
          ],
        }),
      );
    });
  }
}
